import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
export interface GalleryPhoto {
  src: string;
  alt: string;
}
interface GalleryProps {
  photos: GalleryPhoto[];
  /** Cambiar este valor reinicia la galería en la primera foto */
  resetKey?: string;
  badge?: string;
}
export function Gallery({
  photos,
  resetKey,
  badge
}: GalleryProps) {
  const {
    t
  } = useLanguage();
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  useEffect(() => {
    setIndex(0);
    setDirection(1);
  }, [resetKey]);
  const go = (step: number) => {
    setDirection(step);
    setIndex(current => (current + step + photos.length) % photos.length);
  };
  const goTo = (next: number) => {
    setDirection(next > index ? 1 : -1);
    setIndex(next);
  };
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      go(1);
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      go(-1);
    }
  };
  const photo = photos[index];
  return <div className="relative rounded-2xl overflow-hidden bg-primary-dark" role="group" aria-roledescription="carousel" aria-label={t('Galería de fotos', 'Photo gallery')} tabIndex={0} onKeyDown={handleKeyDown} data-id="element-202" data-mp="112mbjv91p2nk">
      <div className="relative aspect-[4/3] sm:aspect-[16/10]" data-id="element-203" data-mp="8fjjnlyvjq6a">
        <AnimatePresence initial={false} custom={direction} mode="popLayout" data-id="element-204" data-mp="dc0a4mtjqgmj">
          <motion.img key={photo.src} src={photo.src} alt={photo.alt} custom={direction} initial={{
          opacity: 0,
          x: direction * 24
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: direction * -24
        }} transition={{
          duration: 0.25,
          ease: [0.23, 1, 0.32, 1]
        }} className="absolute inset-0 w-full h-full object-cover" data-id="element-205" data-mp="hen5uytc8lp0" />
        </AnimatePresence>

        {badge && <span className="absolute top-4 left-4 bg-white text-primary text-xs font-medium px-3 py-1.5 rounded-full" data-id="element-206" data-mp="3pu6f9pnwjn58">
            {badge}
          </span>}

        <span className="absolute top-4 right-4 bg-primary-dark/70 text-white text-xs font-medium px-3 py-1.5 rounded-full tabular-nums" data-id="element-207" data-mp="7ns4fzkxjtb2">
          {index + 1} / {photos.length}
        </span>

        {photos.length > 1 && <>
            <button type="button" onClick={() => go(-1)} aria-label={t('Foto anterior', 'Previous photo')} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 text-primary flex items-center justify-center hover:bg-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent" data-id="element-208" data-mp="366guw3iaop61">
              <ChevronLeft className="w-5 h-5" data-id="element-209" data-mp="2nghd81osaccn" />
            </button>
            <button type="button" onClick={() => go(1)} aria-label={t('Foto siguiente', 'Next photo')} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 text-primary flex items-center justify-center hover:bg-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent" data-id="element-210" data-mp="12xnbqr9q7vwe">
              <ChevronRight className="w-5 h-5" data-id="element-211" data-mp="1lo4z0gbshzad" />
            </button>
          </>}
      </div>

      {photos.length > 1 && <div className="flex gap-2 p-3 bg-white overflow-x-auto" data-id="element-212" data-mp="1qm6l7a4iieq2">
          {photos.map((item, i) => <button key={item.src} type="button" onClick={() => goTo(i)} aria-label={t(`Ver foto ${i + 1}`, `View photo ${i + 1}`)} aria-current={i === index} className={`relative w-24 shrink-0 aspect-[16/10] rounded-lg overflow-hidden transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-accent ${i === index ? 'ring-2 ring-accent' : 'opacity-60 hover:opacity-100'}`} data-id="element-213" data-mp="1cmopeassu177" data-mpi={i}>
              <img src={item.src} alt="" className="w-full h-full object-cover" data-id="element-214" data-mp="gp9qb6hqg5e0" />
            </button>)}
        </div>}
    </div>;
}